/* Created by Interactive 63 - https://interactive63.com */
/**
 * Editor playback. The scene is compiled with the same compiler the export uses, injected into
 * the stage, and driven through the Web Animations API: one reference animation acts as the
 * clock and every CSS animation in the stage is kept at the clock's time.
 */
import { compileScene } from '../core/compiler/compile';
import { createMorphPlayer, type MorphPlayer } from '../runtime/morph-player';
import { holdsAfterFirstPass, onceAnimationNames } from './playback-pass';
import { setPlayhead } from './store/actions';
import type { Store } from './store/store';
import type { StagePanel } from './panels/stage/stage';

import type { Project } from '../core/model/types';
import { toast } from './ui/toast';

const COMPILE_BUDGET_MS = 8;

export type PlaybackRate = 0.25 | 0.5 | 1 | 2;

export class PlaybackController {
  private stage: StagePanel | null = null;
  private clock: Animation | null = null;
  private clockDuration = 0;
  private playing = false;
  private loop = true;
  private rate: PlaybackRate = 1;
  private listeners = new Set<(ms: number) => void>();
  private tickFrame = 0;
  private compileFrame = 0;
  private writingPlayhead = false;
  private passes = 0;
  private morph: MorphPlayer | null = null;
  private compileFailed = false;
  /** Turns the project into CSS and markup. Replaceable so tests can simulate a failure. */
  compiler: typeof compileScene = compileScene;

  /** Creates a controller for a store. Call `attachStage` once the stage is mounted. */
  constructor(private readonly store: Store) {}

  /** Iteration-local time in milliseconds. */
  get currentTime(): number {
    const time = this.clock?.currentTime;
    return typeof time === 'number' ? time : this.store.getState().editor.playhead;
  }

  /** Zero-based count of completed loops in the current run; tracks that play once hold from 1. */
  get pass(): number {
    return this.passes;
  }

  /** Whether playback is running. */
  get isPlaying(): boolean {
    return this.playing;
  }

  /** Connects the controller to the stage and compiles the first scene. */
  attachStage(stage: StagePanel): void {
    this.stage = stage;
    stage.onSceneReplaced(() => {
      this.sync();
    });
    this.store.select(
      (s) => s.project,
      () => {
        this.scheduleCompile();
      },
    );
    this.store.select(
      (s) => s.editor.playhead,
      (playhead) => {
        if (!this.writingPlayhead && Math.abs(playhead - this.currentTime) > 0.5) {
          this.passes = 0;
          this.applyTime(playhead);
        }
      },
    );
    this.onTick(() => {
      stage.redrawOverlay();
    });
    this.compileNow();
  }

  /** Starts playback, restarting from the beginning if the playhead is at the end. */
  play(): void {
    if (!this.stage) return;
    if (this.currentTime >= this.duration() - 0.5) {
      this.passes = 0;
      this.applyTime(0);
    }
    this.playing = true;
    this.sync();
    this.store.updateEditor((draft) => {
      draft.isPlaying = true;
    });
    this.scheduleTick();
  }

  /** Pauses playback and records the playhead in the store. */
  pause(): void {
    const wasPlaying = this.playing;
    this.playing = false;
    cancelAnimationFrame(this.tickFrame);
    this.tickFrame = 0;
    this.sync();
    this.writePlayhead(this.currentTime);
    if (wasPlaying || this.store.getState().editor.isPlaying) {
      this.store.updateEditor((draft) => {
        draft.isPlaying = false;
      });
    }
    this.emit();
  }

  /** Pauses and returns to the start. */
  stop(): void {
    this.pause();
    this.seek(0);
  }

  /** Jumps to a time, clamped to the scene, and records it as the playhead. */
  seek(ms: number): void {
    this.passes = 0;
    this.applyTime(ms);
    this.writePlayhead(this.currentTime);
  }

  /** Moves the clock while scrubbing, without writing the playhead to the store. */
  scrub(ms: number): void {
    this.passes = 0;
    this.applyTime(ms);
  }

  /** Turns looping on or off. */
  setLoop(loop: boolean): void {
    this.loop = loop;
  }

  /** Changes the playback speed. */
  setRate(rate: PlaybackRate): void {
    this.rate = rate;
    this.sync();
  }

  /** Subscribes to time updates. Fires every frame while playing and on every seek. */
  onTick(listener: (ms: number) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Scene duration in milliseconds. */
  private duration(): number {
    return this.store.getState().project.scene.duration;
  }

  /** Sets the time on the clock and every CSS animation without touching the store. */
  private applyTime(ms: number): void {
    const time = Math.min(this.duration(), Math.max(0, ms));
    for (const animation of this.animations()) animation.currentTime = time;
    this.emit();
  }

  /** Stores the playhead, flagging the write so the store subscription ignores it. */
  private writePlayhead(ms: number): void {
    this.writingPlayhead = true;
    try {
      setPlayhead(this.store, ms);
    } finally {
      this.writingPlayhead = false;
    }
  }

  /** Notifies tick listeners with the current time. */
  private emit(): void {
    const time = this.currentTime;
    this.morph?.render();
    for (const listener of this.listeners) listener(time);
  }

  /** Compiles once per animation frame, however many project changes arrive. */
  private scheduleCompile(): void {
    if (this.compileFrame) return;
    this.compileFrame = requestAnimationFrame(() => {
      this.compileFrame = 0;
      this.compileNow();
    });
  }

  /** Compiles the project and shows the result on the stage. */
  private compileNow(): void {
    if (!this.stage) return;
    const { project } = this.store.getState();
    const compiled = this.compileSafely(project);
    if (!compiled) return;
    this.ensureClock(project.scene.duration);
    this.stage.setScene({
      markup: compiled.svgMarkup,
      css: compiled.css,
      sceneClass: compiled.sceneClass,
      viewBox: project.svg.viewBox,
    });
    // Morphs are drawn by the same player the export uses, so previews match exports.
    this.morph = createMorphPlayer(this.stage.getArtboard(), compiled.morphs, () => ({
      time: this.currentTime,
      iteration: this.passes,
    }));
    // New CSS can create fresh animations; bring them to the clock's time in the same frame.
    this.sync();
    this.stage.redrawOverlay();
  }

  /**
   * Compiles for the stage. If the compiler throws, the last good preview stays on screen and a
   * single warning is shown until a compile succeeds again.
   */
  private compileSafely(project: Project): ReturnType<typeof compileScene> | null {
    try {
      const started = performance.now();
      const compiled = this.compiler(project, {
        target: 'preview',
        exportSettings: project.export,
      });
      if (import.meta.env.DEV) {
        const elapsed = performance.now() - started;
        performance.measure('svg-tempo:compile', { start: started, duration: elapsed });
        if (elapsed > COMPILE_BUDGET_MS) {
          console.warn(
            `compileScene took ${elapsed.toFixed(1)} ms (budget ${COMPILE_BUDGET_MS} ms)`,
          );
        }
      }
      this.compileFailed = false;
      return compiled;
    } catch (error) {
      console.error(error);
      if (!this.compileFailed) {
        this.compileFailed = true;
        toast.warning("The preview couldn't be updated. The last good version is still showing.");
      }
      return null;
    }
  }

  /** Creates the reference clock, or recreates it when the duration changes, keeping the time. */
  private ensureClock(duration: number): void {
    if (!this.stage || (this.clock && this.clockDuration === duration)) return;
    const previous = this.clock
      ? Number(this.clock.currentTime ?? 0)
      : this.store.getState().editor.playhead;
    this.clock?.cancel();
    this.clock = this.stage.getArtboard().animate([], { duration, fill: 'both' });
    this.clock.currentTime = Math.min(duration, previous);
    this.clock.pause();
    this.clockDuration = duration;
  }

  /** The clock plus every CSS animation currently running in the stage. */
  private animations(): Animation[] {
    if (!this.stage) return [];
    const css = this.stage
      .getArtboard()
      .getAnimations({ subtree: true })
      .filter((animation) => animation !== this.clock && animation instanceof CSSAnimation);
    return this.clock ? [this.clock, ...css] : css;
  }

  /** Makes every animation share the clock's time, speed and play state. */
  private sync(): void {
    const time = this.currentTime;
    const once = this.passes > 0 ? onceAnimationNames(this.store.getState().project) : null;
    for (const animation of this.animations()) {
      animation.playbackRate = this.rate;
      // From the second loop, tracks that play once stay at their end pose, as in a looping export.
      if (
        once &&
        animation instanceof CSSAnimation &&
        holdsAfterFirstPass(animation.animationName, once, this.passes)
      ) {
        animation.pause();
        animation.currentTime = this.duration();
        continue;
      }
      if (this.playing) {
        animation.currentTime = time;
        animation.play();
      } else {
        animation.pause();
        animation.currentTime = time;
      }
    }
    this.morph?.render();
  }

  /** Queues the next frame of the playback loop. */
  private scheduleTick(): void {
    this.tickFrame = requestAnimationFrame(() => {
      this.tick();
    });
  }

  /** Reports the time and handles reaching the end of the scene. */
  private tick(): void {
    if (!this.playing) return;
    const time = this.currentTime;
    this.emit();
    if (time >= this.duration() - 0.5) {
      if (this.loop) {
        this.passes += 1;
        this.applyTime(0);
        this.sync();
      } else {
        this.pause();
        return;
      }
    }
    this.scheduleTick();
  }
}
